import React from 'react';

/** Renders as "3 of 5 items", or "5 items" when all are affected. */
export type AccessoryCount = {
    readonly type: 'count';
    affected: number;
    /** All selected items. */
    total: number;
};

export function isAccessoryCount(value: unknown): value is AccessoryCount {
    return typeof value === 'object' && value !== null && 'type' in value && (value as AccessoryCount).type === 'count';
}

/** A node, or separate icons for the default and the hovered or selected state. */
export type CommandIcon =
    | React.ReactNode
    | {
          default: React.ReactNode;
          /** For the hovered or selected state; defaults to `default`. */
          hover?: React.ReactNode;
      };

export function isIconConfig(value: unknown): value is { default: React.ReactNode; hover?: React.ReactNode } {
    return typeof value === 'object' && value !== null && 'default' in value;
}

/** An inline confirmation step before the command runs. */
export type CommandConfirmation = {
    title: () => string;
    description?: () => string;
    /** Defaults to "Confirm". */
    confirmLabel?: () => string;
    /** Defaults to "Cancel". */
    cancelLabel?: () => string;
    /** `'destructive'` shows the confirm option in red. */
    variant?: 'default' | 'destructive';
};

/** A preview panel that slides in from the right when the user presses → on the selected command. */
export type QuickLookConfig<TItem = unknown, TSharedContext = unknown> = {
    render: (ctx: CommandContext<TItem, TSharedContext>) => React.ReactNode;
    /** In pixels; defaults to 360. */
    width?: number;
    /** Accessory text that turns into a "Preview" button on hover; without it, only the button shows, on hover. */
    preview?: string | ((ctx: CommandContext<TItem, TSharedContext>) => string);
};

export type CommandContext<TItem = unknown, TSharedContext = unknown> = {
    /** All selected items, those hidden by filters included: actions apply to the whole selection. */
    selectedItems: TItem[];
    /** Selected items hidden by the filters; tell the user when it isn't 0. */
    hiddenSelectedCount: number;
    /** The table's `sharedContext`. */
    sharedContext?: TSharedContext;
    close: () => void;
    clearSelection: () => void;
    /** Back to the parent command's level, for subcommands. */
    goBack: () => void;
};

export type CommandDefinition<TItem = unknown, TSharedContext = unknown> = {
    id: string;
    /** A function, like every text prop here, so it translates at render. */
    label: () => string;
    description?: () => string;
    /** cmdk filters on these too. */
    keywords?: string[];
    /** A node, or `{ default, hover }` to swap icons while the item is hovered or selected. */
    icon?: CommandIcon;
    group?: () => string;
    /** Like ['⌘', 'D']; shown on the item, and runs it while the palette is open. */
    shortcut?: string[];
    /** Shown on the right, e.g. a status or a count. */
    accessory?:
        | React.ReactNode
        | AccessoryCount
        | ((ctx: CommandContext<TItem, TSharedContext>) => React.ReactNode | AccessoryCount);
    disabled?: boolean | ((ctx: CommandContext<TItem, TSharedContext>) => boolean);
    visible?: boolean | ((ctx: CommandContext<TItem, TSharedContext>) => boolean);
    /** Subcommands, shown as a new page when the command is selected. */
    children?:
        | CommandDefinition<TItem, TSharedContext>[]
        | ((ctx: CommandContext<TItem, TSharedContext>) => CommandDefinition<TItem, TSharedContext>[]);
    /** Shown, with a fixed icon, when `children` returns an empty array. */
    emptyPlaceholder?: {
        title: () => string;
        description?: () => string;
    };
    /** Heads this command's sub-page when that should read differently from the command; defaults to its label. */
    subPageHeader?: {
        title: () => string;
        description?: () => string;
    };
    /** Not needed when `children` is set. */
    onSelect?: (ctx: CommandContext<TItem, TSharedContext>) => void | Promise<void>;
    /** Closes the palette after a successful run. */
    closeOnComplete?: boolean;
    /** Runs the command only once confirmed; the palette stays open with a spinner on Confirm while it runs. */
    confirmation?: CommandConfirmation;
    quickLook?: QuickLookConfig<TItem, TSharedContext>;
};

export type CommandPaletteProps<TItem = unknown, TSharedContext = unknown> = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    commands: CommandDefinition<TItem, TSharedContext>[];
    context: Omit<CommandContext<TItem, TSharedContext>, 'close' | 'clearSelection' | 'goBack'>;
    /** Passed by TanStackTable. */
    onClearSelection?: () => void;
    placeholder?: string;
    /** Defaults to "N items selected". */
    renderSelectionHeader?: (props: { selectedItems: TItem[]; isSingleSelection: boolean }) => React.ReactNode;
    // Context ids such as `rfq_id` or `sourcing_scenario_id` go in attributes of their own.
    /** Sent with every Sentry metric; `table_id` is `{module}/{table}`, such as `sourcing/line_items`. */
    telemetry?: Record<string, string | number | boolean>;
};
